import React, { useState } from 'react';
import { Plus, Search, Filter, ChevronDown, Check, Mail, Share } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { WishItem } from '@/utils/storage';
import WishListItem from './WishListItem';
import AddWishListItem from './AddWishListItem';

interface WishListTabProps {
  wishList: WishItem[];
  availableStars: number;
  totalStarsEarned: number;
  walletData?: {
    currentBalance: number;
    lifetimeEarned: number;
  };
  walletSettings?: {
    enabled: boolean;
    conversionRate: number;
  };
  editMode: boolean;
  onAddItem: (item: Omit<WishItem, 'id' | 'dateAdded'>) => void;
  onEditItem: (id: string, updates: Partial<WishItem>) => void;
  onDeleteItem: (id: string) => void;
  onMarkAchieved: (id: string) => void;
  onUpdateProgress: (id: string, progress: number) => void;
  onReAddItem?: (id: string) => void;
}

const WishListTab: React.FC<WishListTabProps> = ({
  wishList,
  availableStars,
  totalStarsEarned,
  walletData,
  walletSettings,
  editMode,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onMarkAchieved,
  onUpdateProgress,
  onReAddItem
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'unachieved' | 'achieved'>('unachieved');
  const [sortBy, setSortBy] = useState<string>('dateAdded');

  // Filter and sort wish list items
  const filteredWishList = wishList
    .filter(item => {
      // Search filter
      if (searchTerm && !item.title.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      
      // Status filter
      if (filterStatus === 'achieved' && !item.achieved) {
        return false;
      }
      if (filterStatus === 'unachieved' && item.achieved) {
        return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'cost':
          return a.cost - b.cost;
        case 'progress':
          return b.progress - a.progress;
        case 'dateAdded':
          return new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime();
        case 'alphabetical':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

  // Calculate affordability for each item
  const getAffordability = (cost: number) => {
    if (walletSettings?.enabled && walletData) {
      return (walletData.currentBalance / cost) * 100;
    } else {
      return (availableStars / cost) * 100;
    }
  };

  const getCurrency = () => {
    return walletSettings?.enabled ? '$' : 'stars';
  };

  const getCurrentBalance = () => {
    return walletSettings?.enabled ? walletData?.currentBalance || 0 : availableStars;
  };

  // Email sharing functionality
  const generateWishListEmail = () => {
    const unachievedItems = wishList.filter(item => !item.achieved);
    const currency = getCurrency();
    const currentBalance = getCurrentBalance();
    
    let emailBody = `Hi! I wanted to share my Star Chores wish list with you:\n\n`;
    emailBody += `Current balance: ${currentBalance} ${currency}\n\n`;
    emailBody += `My Wish List:\n`;
    
    unachievedItems.forEach((item, index) => {
      emailBody += `${index + 1}. ${item.title}\n`;
      emailBody += `   Cost: ${item.cost} ${currency}\n`;
      if (item.description) {
        emailBody += `   Description: ${item.description}\n`;
      }
      if (item.purchaseLink) {
        emailBody += `   Where to buy: ${item.purchaseLink}\n`;
      }
      emailBody += `   Progress: ${item.progress}%\n\n`;
    });
    
    emailBody += `Thanks for helping me save up for my goals! 💝\n\n`;
    emailBody += `Generated from Star Chores app`;
    
    const subject = `My Star Chores Wish List (${unachievedItems.length} items)`;
    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;
    
    window.open(mailtoLink);
  };

  if (wishList.length === 0 && !showAddForm) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="py-12">
            <div className="text-center space-y-4">
              <div className="text-5xl">💝</div>
              <h3 className="text-lg font-medium">Your Wish List is Empty</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Start adding items you want to save up for! You can track your progress and see how close you are to affording your dreams.
              </p>
              <Button 
                onClick={() => setShowAddForm(true)}
                className="mt-4"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Your First Wish
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with current balance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">💝 My Wish List</div>
            <div className="flex items-center gap-3">
              {wishList.filter(item => !item.achieved).length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generateWishListEmail}
                  className="gap-2"
                >
                  <Mail className="h-4 w-4" />
                  Share List
                </Button>
              )}
              <div className="text-sm font-normal text-muted-foreground">
                {getCurrentBalance()} {getCurrency()}
              </div>
            </div>
          </CardTitle>
        </CardHeader>
      </Card>

      {/* Add new item form */}
      {showAddForm && (
        <AddWishListItem
          onAdd={(item) => {
            onAddItem(item);
            setShowAddForm(false);
          }}
          onCancel={() => setShowAddForm(false)}
          walletEnabled={walletSettings?.enabled || false}
        />
      )}

      {/* Controls */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Search and Add Button */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search wish list..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button 
                onClick={() => setShowAddForm(true)}
                disabled={showAddForm}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Item
              </Button>
            </div>

            {/* Status Toggle and Sort Pills */}
            <div className="flex items-center justify-between">
              {/* Status Toggle */}
              <div className="flex gap-2">
                <Button
                  variant={filterStatus === 'unachieved' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('unachieved')}
                >
                  Wanting
                </Button>
                <Button
                  variant={filterStatus === 'achieved' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('achieved')}
                >
                  Achieved
                </Button>
              </div>

              {/* Sort Pills */}
              <div className="flex gap-2">
                <span className="text-sm text-muted-foreground">Sort by:</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      {sortBy === 'dateAdded' && 'Date Added'}
                      {sortBy === 'cost' && 'Cost'}
                      {sortBy === 'progress' && 'Progress'}
                      {sortBy === 'alphabetical' && 'A-Z'}
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-40 p-1">
                    <div className="space-y-1">
                      {[
                        { value: 'dateAdded', label: 'Date Added' },
                        { value: 'cost', label: 'Cost' },
                        { value: 'progress', label: 'Progress' },
                        { value: 'alphabetical', label: 'A-Z' }
                      ].map((option) => (
                        <Button
                          key={option.value}
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start gap-2"
                          onClick={() => setSortBy(option.value)}
                        >
                          {sortBy === option.value && <Check className="h-3 w-3" />}
                          <span className={sortBy !== option.value ? 'ml-5' : ''}>{option.label}</span>
                        </Button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Wish List Items */}
      <div className="space-y-4">
        {filteredWishList.map((item) => (
          <WishListItem
            key={item.id}
            item={item}
            affordability={getAffordability(item.cost)}
            currency={getCurrency()}
            currentBalance={getCurrentBalance()}
            editMode={editMode}
            viewMode={filterStatus}
            onEdit={(updates) => onEditItem(item.id, updates)}
            onDelete={() => onDeleteItem(item.id)}
            onMarkAchieved={() => onMarkAchieved(item.id)}
            onUpdateProgress={(progress) => onUpdateProgress(item.id, progress)}
            onReAdd={onReAddItem ? () => onReAddItem(item.id) : undefined}
          />
        ))}
      </div>

      {filteredWishList.length === 0 && wishList.length > 0 && (
        <Card>
          <CardContent className="py-8">
            <div className="text-center space-y-2">
              <Filter className="mx-auto h-8 w-8 text-muted-foreground" />
              <h3 className="font-medium">No items match your filters</h3>
              <p className="text-sm text-muted-foreground">
                Try adjusting your search or filter settings.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WishListTab;